import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { FaMapMarkerAlt, FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';

const mapContainerStyle = { height: '200px', width: '100%' };
const defaultCenter = { lat: -28.4793, lng: 24.6729 }; // South Africa center

// 🌍 Extract the country from a Google geocode result (address_components).
const extractCountry = (result) => {
  if (!result || !result.address_components) return null;
  const comp = result.address_components.find(c => c.types && c.types.includes('country'));
  return comp ? (comp.long_name || comp.short_name || null) : null;
};

// NOTE: country restrictions were removed — One Purpose quotes cross borders
// (Lusaka, Harare, Gaborone, Windhoek, etc.) and array country restrictions on
// the JS Places API are unreliable (some clients only honour the first code,
// e.g. ZA only), which broke searching for destinations like Zambia.

function LocationPicker({ 
  label, 
  value, 
  onChange, 
  onCoordinatesChange, 
  placeholder = 'Enter address...',
  required = false,
  className = '',
  initialCoordinates = null
}) {
  const [coordinates, setCoordinates] = useState(initialCoordinates);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState(value || '');
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimerRef = useRef(null);

  // Update input when parent value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Update coordinates when parent provides new ones (e.g. when editing a saved quote)
  useEffect(() => {
    if (initialCoordinates && initialCoordinates.lat && initialCoordinates.lng) {
      setCoordinates(initialCoordinates);
      if (map) {
        map.panTo({ lat: initialCoordinates.lat, lng: initialCoordinates.lng });
        map.setZoom(14);
      }
    }
  }, [initialCoordinates && initialCoordinates.lat, initialCoordinates && initialCoordinates.lng]);

  // Search for places using Google Places AutocompleteService —
  // instant "search as you type" suggestions (addresses + business names),
  // identical behaviour to the quote page's route picker.
  const searchPlaces = (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.AutocompleteService();
      autocomplete.getPlacePredictions(
        { input: query },
        (predictions, status) => {
          if (status === 'OK' && predictions && predictions.length > 0) {
            setSuggestions(predictions.map((p) => ({
              place_id: p.place_id,
              description: p.description,
              geometry: null,
              country: null
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } catch (error) {
      console.error('Error searching places:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (suggestion) => {
    // Cancel any pending search timer so a stale suggestion list can't reappear.
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    setIsTyping(false);
    onChange(suggestion.description);

    // Predictions don't include coordinates — fetch the place details.
    try {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails(
        { placeId: suggestion.place_id, fields: ['geometry', 'formatted_address', 'address_components'] },
        (place, status) => {
          if (status === 'OK' && place && place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const coords = { lat, lng };
            const finalAddress = place.formatted_address || suggestion.description;
            setCoordinates(coords);
            setInputValue(finalAddress);
            onChange(finalAddress);

            if (map) {
              map.panTo(coords);
              map.setZoom(14);
            }

            if (onCoordinatesChange) {
              onCoordinatesChange({ lat, lng, address: finalAddress, country: extractCountry(place) });
            }
          }
        }
      );
    } catch (error) {
      console.error('Error setting coordinates:', error);
    }
  };

  const handleMapClick = (e) => {
    if (!e || !e.latLng) return;
    
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const coords = { lat, lng };
    setCoordinates(coords);
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const address = results[0].formatted_address;
        setInputValue(address);
        onChange(address);
        if (onCoordinatesChange) {
          onCoordinatesChange({ lat, lng, address, country: extractCountry(results[0]) });
        }
      } else {
        if (onCoordinatesChange) {
          onCoordinatesChange({ lat, lng, address: `${lat}, ${lng}` });
        }
      }
    });
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const coords = { lat, lng };
        setCoordinates(coords);
        
        if (map) {
          map.panTo(coords);
          map.setZoom(14);
        }
        
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const address = results[0].formatted_address;
            setInputValue(address);
            onChange(address);
            if (onCoordinatesChange) {
              onCoordinatesChange({ lat, lng, address, country: extractCountry(results[0]) });
            }
          } else {
            if (onCoordinatesChange) {
              onCoordinatesChange({ lat, lng, address: `${lat}, ${lng}` });
            }
          }
          setLoading(false);
        });
      },
      () => {
        toast.error('Unable to retrieve your location');
        setLoading(false);
      }
    );
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setIsTyping(true);
    onChange(val);
    
    if (val === '') {
      setCoordinates(null);
      setSuggestions([]);
      setShowSuggestions(false);
      if (onCoordinatesChange) {
        onCoordinatesChange(null);
      }
    } else {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => searchPlaces(val), 250);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      // If there are suggestions, select the first one
      if (suggestions.length > 0) {
        handleSelect(suggestions[0]);
      } else {
        // Otherwise, search directly using geocoding
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: inputValue }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const result = results[0];
            const lat = result.geometry.location.lat();
            const lng = result.geometry.location.lng();
            const coords = { lat, lng };
            setCoordinates(coords);
            setShowSuggestions(false);
            
            if (map) {
              map.panTo(coords);
              map.setZoom(14);
            }
            
            if (onCoordinatesChange) {
              onCoordinatesChange({ lat, lng, address: result.formatted_address, country: extractCountry(result) });
            }
          }
        });
      }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={placeholder}
            className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green pl-10"
            style={{ borderColor: '#E8ECEF' }}
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          {inputValue && (
            <button
              type="button"
              onClick={() => {
                setInputValue('');
                setIsTyping(false);
                onChange('');
                setCoordinates(null);
                setSuggestions([]);
                setShowSuggestions(false);
                if (onCoordinatesChange) {
                  onCoordinatesChange(null);
                }
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FaTimes size={14} />
            </button>
          )}
        </div>
        
        <button
          type="button"
          onClick={getLocation}
          disabled={loading}
          className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
        >
          {loading ? <FaSpinner className="animate-spin" /> : <FaMapMarkerAlt />}
          <span className="hidden sm:inline">My Location</span>
        </button>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full bg-white rounded-lg shadow-lg border mt-1 max-h-48 overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors border-b last:border-b-0"
              style={{ borderColor: '#E8ECEF' }}
            >
              {suggestion.description}
            </button>
          ))}
        </div>
      )}

      {/* Map */}
      <div className="relative border rounded-lg overflow-hidden" style={{ height: '200px' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={coordinates || defaultCenter}
          zoom={coordinates ? 14 : 6}
          onClick={handleMapClick}
          onLoad={(mapInstance) => setMap(mapInstance)}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
          }}
        >
          {coordinates && (
            <Marker
              position={coordinates}
              draggable={true}
              onDragEnd={(e) => {
                if (!e || !e.latLng) return;
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                const coords = { lat, lng };
                setCoordinates(coords);
                
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                  if (status === 'OK' && results && results.length > 0) {
                    const address = results[0].formatted_address;
                    setInputValue(address);
                    onChange(address);
                    if (onCoordinatesChange) {
                      onCoordinatesChange({ lat, lng, address, country: extractCountry(results[0]) });
                    }
                  } else {
                    if (onCoordinatesChange) {
                      onCoordinatesChange({ lat, lng, address: `${lat}, ${lng}` });
                    }
                  }
                });
              }}
            />
          )}
        </GoogleMap>
        {coordinates && (
          <div className="absolute bottom-2 left-2 bg-white rounded-lg shadow-md px-3 py-1 text-xs text-gray-600 border">
            📍 {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
          </div>
        )}
        {!coordinates && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-gray-400 bg-white/80 px-4 py-2 rounded-lg">
              Click on the map or search to set location
            </p>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400">Type an address and press Enter, select from dropdown, or click on the map</p>
    </div>
  );
}

export default LocationPicker;
